import type { CSSProperties } from 'react'
import type { ECharts, EChartsOption } from 'echarts'
import { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import { useDomSize } from 'src/hooks'
import 'src/styles/components/common/easyECharts.less'

type Props = {
  option?: EChartsOption,
  notMerge?: boolean,
  lazyUpdate?: boolean,
  autoPlay?: boolean,
  autoPlayTime?: number,
  onEvents?: Record<string, Function>,
  onChartReady?: (instance: ECharts) => void,
  onSizeChange?: (rect: { clientWidth: number, clientHeight: number }) => void,
  style?: CSSProperties,
  className?: string
}

const EasyECharts = (props: Props) => {
  const chart = useRef<ECharts>()
  const refOfDom = useRef(null)

  // 初始化实例
  useEffect(() => {
    if (refOfDom.current) {
      // 开启脏矩阵渲染，提高大数据量的渲染能力
      chart.current = echarts.init(refOfDom.current, undefined, { useDirtyRect: true })
      props.onChartReady?.(chart.current)
    }
    return () => {
      chart.current?.dispose()
      chart.current = undefined
    }
  }, [])

  // 绑定事件
  useEffect(() => {
    if (chart.current && props.onEvents) {
      for (const [key, func] of Object.entries(props.onEvents)) {
        func instanceof Function && chart.current.on(key, (params) => func(params))
      }
    }
  }, [])

  // 自定播放
  const timer = useRef<any>(-1)

  const startPlay = () => {
    if (!props.autoPlay) return

    let dataIndex = 0
    timer.current = setInterval(() => {
      const chartOption = chart.current?.getOption() as any
      const maxDataIndex = chartOption?.series?.[0]?.data.length

      chart.current?.dispatchAction({
        type: 'downplay'
      })
      chart.current?.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex
      })
      chart.current?.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex
      })
      ++dataIndex

      if (maxDataIndex && dataIndex >= maxDataIndex) {
        dataIndex = 0
      }
    }, props.autoPlayTime ?? 3000)
  }

  const stopPlay = () => {
    clearInterval(timer.current)
  }

  useEffect(() => {
    startPlay()

    return () => {
      clearInterval(timer.current)
    }
  }, [props.autoPlayTime])

  // 更新配置文件
  useEffect(() => {
    if (chart.current) {
      if (props.option) {
        chart.current.setOption(props.option, props.notMerge, props.lazyUpdate)
      } else {
        chart.current.clear()
      }
    }
  }, [props.option])

  // 响应布局变化
  const domSize = useDomSize(refOfDom)
  useEffect(() => {
    chart.current?.resize()
    props.onSizeChange?.(domSize)
  }, [domSize])

  return (
    <div
      ref={refOfDom}
      onMouseEnter={stopPlay}
      onMouseLeave={startPlay}
      style={props.style}
      className={`easy-echarts ${props.className}`}
    />
  )
}

export default EasyECharts